import { useEffect } from 'react';
import { useIsRestoring } from '@tanstack/react-query';
import { usePathname } from 'core/i18n/navigation';
import { requestToCheckSelection } from '../actors/SpaceActor';
import { prefetchSpaceRoute } from 'networking/mindfeed/queries/spaceRoute';
import { LoadMark, markLoad } from 'utils/PerformanceUtils';
import { appRoutes, type SpaceRoute } from 'config/routing';
import { useSpaceRoute } from 'hooks/useSpaceRoute';

/**
 * Initialisation + route synchronisation for the Space state machine.
 *
 * - On a `/spaces/*` route, drive the selection from the URL params.
 * - On any other route, fall back to the previously stored selection.
 *
 * Runs once at the layout level. Uses next-intl's usePathname (locale-agnostic,
 * so `/au/spaces` resolves to `/spaces`) and useSpaceRoute for the parsed
 * catch-all slug. (App Router params are ready synchronously, so the Pages
 * Router `router.isReady` guard is no longer needed.)
 *
 * The saved cache comes back from localStorage a moment after the first
 * render. Check the URL only once it's back, so the box is found in it and
 * shown at once, instead of being fetched from the network. Call it inside
 * PersistQueryClientProvider (see hocs/SpaceAutoSelector).
 *
 * The requests for the box don't wait for that, though. Waiting for the
 * cache also means waiting for the page to re-render with it, which took
 * about 150 ms on a reload. So the requests go out as soon as the URL is
 * known, and the newer answers replace the saved copy when they arrive.
 */
export const useSpaceAutoSelector = () => {
  const pathname = usePathname();
  const route = useSpaceRoute();
  const isRestoring = useIsRestoring();

  const isOnDashboard = pathname.startsWith(appRoutes.MEMORY_SPACE);
  // A malformed dashboard URL can't be followed: it lands like /spaces.
  const routeToFollow = isOnDashboard && route !== null ? route : SPACES_ROUTE;

  // Start the box's requests now, before the saved cache is back.
  useEffect(() => {
    if (!isOnDashboard) {
      return;
    }
    void prefetchSpaceRoute(routeToFollow);
  }, [isOnDashboard, routeToFollow]);

  useEffect(() => {
    if (isRestoring) {
      return;
    }
    markLoad(LoadMark.SelectionChecked);
    requestToCheckSelection(routeToFollow);
  }, [routeToFollow, isRestoring]);
};

// Off the dashboard there is no URL to follow, so the stored selection
// stands, as when landing on /spaces. One shared value, so the effects above
// don't see a new route on every render.
const SPACES_ROUTE: SpaceRoute = { kind: 'spaces' };
